import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  forkJoin,
  last,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { CreatorService } from '../../../../services/creator/creator.service';
import {
  CreatorModerationStatusEnum,
  CreatorResponseDto,
} from '../../../../common/general-types/creators.dto';
import { BusinessAccount } from '../../../../common/general-types/business-account.dto';
import { DeliveryAddressResponseDto } from '../../../../common/general-types/briefs.dto';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { CopyId } from '../../../../atoms/copy-id/copy-id';
import { ImageService } from '../../../../services/image/image.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { SuperSocialService } from '../../../../services/super-social/super-social.service';
import { S3uploaderService } from '../../../../services/s3uploader/s3uploader.service';
import { ImageTypesEnum, S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { AccountService } from '../../../../services/account/account.service';
import {
  BrandResponseDto,
  BusinessAccountService,
} from '../../../../services/business-account/business-account.service';
import { BusinessCreatorsService } from '../../../../shout-plus/services/business-creators/business-creators.service';
import { InvitesService } from '../../../../shout-plus/services/invites/invites.service';
import { ShoutPlusCreator } from '../../../../shout-plus/models/business-creator';
import {
  formatShoutPlusBusinessCreatorStatusLabel,
  shoutPlusBusinessCreatorStatusPillClass,
  ShoutPlusBusinessCreatorStatusEnum,
} from '../../../../shout-plus/models/shout-plus';
import { AdminBriefTrackerService } from '../../settings/services/admin-brief-tracker.service';
import { AdminCreatorRegistrationService } from '../../settings/services/admin-creator-registration.service';
import {
  BriefTrackerSuperSocialAccount,
  BriefTrackerSuperSocialPlatform,
  BriefTrackerSuperSocialProfile,
} from '../../settings/molecules/brief-tracker/brief-tracker.types';
import {
  formatBriefTrackerNumber,
  normalizeSuperSocialHandleInput,
  superSocialAccountHandleValue,
  superSocialAccountWasUpdated,
  superSocialHandlesMatch,
  superSocialUpdatedDateColour,
  supportsSuperSocialAccountRefresh,
} from '../../settings/molecules/brief-tracker/brief-tracker.helpers';
import {
  ModerateService,
  ModerationStatusDto,
} from '../../../../services/moderate/moderate';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../services/user/user.service';
import { UserResponseDto } from '../../../../services/auth/auth.types';

type ShoutPlusBrandPick = {
  id: string;
  companyName: string;
  companyLogo?: string | null;
};

const BUSINESS_ACCOUNT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type CountryCodeOption = 'US' | 'GB' | 'IE';
type CurrencyCodeOption = 'usd' | 'gbp' | 'eur';

type FlagSelectOption<T extends string> = {
  value: T;
  label: string;
  flagUrl: string;
};

type CreatorProfileFormModel = {
  bio: string;
  rank: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  homeTownName: string;
  countryCode: string;
  currencyCode: string;
  deliveryFullName: string;
  deliveryPostcode: string;
  deliveryAddressLine1: string;
  deliveryAddressLine2: string;
  deliveryTown: string;
  deliveryCountry: string;
};

type SocialPlatformKey = BriefTrackerSuperSocialPlatform | 'snapchat';

type SavableProfileSnapshot = {
  bio: string;
  rank: number;
  firstName: string;
  lastName: string;
  mobile: string;
  homeTownName: string;
  countryCode: string;
  currencyCode: string;
  handles: Record<SocialPlatformKey, string>;
};

const FLAG_BASE = 'https://shout-images-static.s3.eu-west-1.amazonaws.com';

const COUNTRY_OPTIONS: readonly FlagSelectOption<CountryCodeOption>[] = [
  { value: 'US', label: 'United States', flagUrl: `${FLAG_BASE}/flag-usa.png` },
  { value: 'GB', label: 'United Kingdom', flagUrl: `${FLAG_BASE}/flag-uk.png` },
  { value: 'IE', label: 'Ireland', flagUrl: `${FLAG_BASE}/flag-ireland.png` },
];

const CURRENCY_OPTIONS: readonly FlagSelectOption<CurrencyCodeOption>[] = [
  { value: 'usd', label: 'USD', flagUrl: `${FLAG_BASE}/flag-usa.png` },
  { value: 'gbp', label: 'GBP', flagUrl: `${FLAG_BASE}/flag-uk.png` },
  { value: 'eur', label: 'EUR', flagUrl: `${FLAG_BASE}/flag-ireland.png` },
];

function normalizeCurrencyCode(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function normalizeCountryCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

type ProfileEditBaseline = {
  savable: SavableProfileSnapshot;
};

type StatusFlagsDraft = {
  isActive: boolean;
  mobileVerified: boolean;
  isDeleted: boolean;
};

type ForceModerationResponse = {
  data?: CreatorResponseDto[];
};

interface SocialPlatformConfig {
  key: SocialPlatformKey;
  label: string;
  superSocialSupported: boolean;
  followersLabel: string;
}

const SOCIAL_PLATFORMS: SocialPlatformConfig[] = [
  { key: 'instagram', label: 'Instagram', superSocialSupported: true, followersLabel: 'Instagram followers' },
  { key: 'tiktok', label: 'TikTok', superSocialSupported: true, followersLabel: 'TikTok followers' },
  { key: 'youtube', label: 'YouTube', superSocialSupported: true, followersLabel: 'YouTube subscribers' },
  { key: 'snapchat', label: 'Snapchat', superSocialSupported: false, followersLabel: 'Snapchat followers' },
];

const emptyCreatorProfileModel = (): CreatorProfileFormModel => ({
  bio: '',
  rank: 0,
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  homeTownName: '',
  countryCode: '',
  currencyCode: '',
  deliveryFullName: '',
  deliveryPostcode: '',
  deliveryAddressLine1: '',
  deliveryAddressLine2: '',
  deliveryTown: '',
  deliveryCountry: '',
});

const emptyCreatorHandleEdits = (): Record<SocialPlatformKey, string> => ({
  instagram: '',
  tiktok: '',
  youtube: '',
  snapchat: '',
});

function normalizeSavableHandles(handles: Record<SocialPlatformKey, string>): Record<SocialPlatformKey, string> {
  return {
    instagram: normalizeSuperSocialHandleInput(handles.instagram),
    tiktok: normalizeSuperSocialHandleInput(handles.tiktok),
    youtube: normalizeSuperSocialHandleInput(handles.youtube),
    snapchat: normalizeSuperSocialHandleInput(handles.snapchat),
  };
}

function savableSnapshotsEqual(a: SavableProfileSnapshot, b: SavableProfileSnapshot): boolean {
  return (
    a.bio === b.bio &&
    a.rank === b.rank &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.mobile === b.mobile &&
    a.homeTownName === b.homeTownName &&
    a.countryCode === b.countryCode &&
    a.currencyCode === b.currencyCode &&
    a.handles.instagram === b.handles.instagram &&
    a.handles.tiktok === b.handles.tiktok &&
    a.handles.youtube === b.handles.youtube &&
    a.handles.snapchat === b.handles.snapchat
  );
}

@Component({
  selector: 'admin-user-profile',
  imports: [LoadingInlineComponent, FormField, DecimalPipe, DatePipe, CopyId],
  templateUrl: './user-profile.html',
  host: { class: 'block h-full w-full min-w-0 bg-transparent' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserProfile implements OnInit, OnDestroy {
  /** When embedded (e.g. creator insights), pass Firebase UID directly. */
  userId = input<string | null>(null);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private creatorService = inject(CreatorService);
  private trackerService = inject(AdminBriefTrackerService);
  private adminCreatorService = inject(AdminCreatorRegistrationService);
  private superSocialService = inject(SuperSocialService);
  private toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  private uploadService = inject(S3uploaderService);
  private accountService = inject(AccountService);
  private businessAccountService = inject(BusinessAccountService);
  private businessCreatorsService = inject(BusinessCreatorsService);
  private invitesService = inject(InvitesService);
  private moderateService = inject(ModerateService);
  private userService = inject(UserService);
  imageService = inject(ImageService);

  readonly socialPlatforms = SOCIAL_PLATFORMS;
  readonly countryOptions = COUNTRY_OPTIONS;
  readonly currencyOptions = CURRENCY_OPTIONS;
  readonly formatNumber = formatBriefTrackerNumber;
  readonly superSocialUpdatedDateColour = superSocialUpdatedDateColour;
  readonly superSocialAccountHandleValue = superSocialAccountHandleValue;
  readonly superSocialHandlesMatch = superSocialHandlesMatch;
  readonly supportsSuperSocialAccountRefresh = supportsSuperSocialAccountRefresh;
  protected readonly CreatorModerationStatusEnum = CreatorModerationStatusEnum;

  readonly selectedCountryOption = computed(() => {
    const code = normalizeCountryCode(this.creatorProfileModel().countryCode);
    return this.countryOptions.find((option) => option.value === code) ?? null;
  });

  readonly selectedCurrencyOption = computed(() => {
    const code = normalizeCurrencyCode(this.creatorProfileModel().currencyCode);
    return this.currencyOptions.find((option) => option.value === code) ?? null;
  });

  subscriptionList = new Subscription();
  loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  superSocialLoading = signal(false);
  savingState = signal(false);
  emailSavingState = signal(false);
  passwordResetSendingState = signal(false);
  photoUploadingState = signal(false);
  statusFlagsSavingState = signal(false);
  moderationRerunningState = signal(false);
  moderationHistoryLoading = signal(false);
  editMode = signal(false);
  emailJustUpdated = signal(false);
  statusFlagsDraft = signal<StatusFlagsDraft | null>(null);
  latestModeration = signal<ModerationStatusDto | null>(null);

  /** True when this page is opened via `/admin-options/user-profile/:userId` (not embedded). */
  readonly showCreatorsFilterBack = computed(
    () => !this.userId() && this.profileKind() !== 'brand' && !this.profileReturnUrl,
  );
  readonly showProfileReturnBack = computed(() => !this.userId() && !!this.profileReturnUrl);

  /** Captured during navigation so Back can remount creators-v4-filter at the same URL. */
  private readonly creatorsFilterReturnUrl: string | undefined = (() => {
    const fromNav = (
      this.router.getCurrentNavigation()?.extras?.state as { creatorsFilterReturnUrl?: string } | undefined
    )?.creatorsFilterReturnUrl;
    if (fromNav) {
      return fromNav;
    }
    return (history.state as { creatorsFilterReturnUrl?: string } | null)?.creatorsFilterReturnUrl;
  })();

  private readonly profileReturnUrl: string | undefined = (() => {
    const fromNav = (
      this.router.getCurrentNavigation()?.extras?.state as { profileReturnUrl?: string } | undefined
    )?.profileReturnUrl;
    if (fromNav) {
      return fromNav;
    }
    return (history.state as { profileReturnUrl?: string } | null)?.profileReturnUrl;
  })();

  creator = signal<CreatorResponseDto | null>(null);
  brandUser = signal<UserResponseDto | null>(null);
  readonly profileKind = computed(() => {
    if (this.creator()) {
      return 'creator';
    }
    if (this.brandUser()) {
      return 'brand';
    }
    return null;
  });
  deliveryAddress = signal<DeliveryAddressResponseDto | null>(null);
  superSocialProfile = signal<BriefTrackerSuperSocialProfile | null>(null);
  superSocialRefreshLoading = signal<Record<string, boolean>>({});
  superSocialRefreshSent = signal<Record<string, boolean>>({});
  removeAccountLoading = signal<Record<string, boolean>>({});
  creatorHandleEdits = signal<Record<SocialPlatformKey, string>>(emptyCreatorHandleEdits());
  syncFromSuperSocial = signal<Record<SocialPlatformKey, boolean>>({
    instagram: false,
    tiktok: false,
    youtube: false,
    snapchat: false,
  });

  creatorProfileModel = signal<CreatorProfileFormModel>(emptyCreatorProfileModel());
  creatorProfileForm = form(this.creatorProfileModel);
  editBaseline = signal<ProfileEditBaseline | null>(null);

  readonly selectedShoutPlusBrand = signal<ShoutPlusBrandPick | null>(null);
  readonly selectedBusinessId = computed(() => this.selectedShoutPlusBrand()?.id ?? null);
  readonly shoutPlusBrandSearch = signal('');
  readonly shoutPlusBrandResults = signal<BusinessAccount[]>([]);
  readonly shoutPlusBrandSearchLoading = signal(false);
  readonly shoutPlusBrandPickerOpen = signal(false);
  readonly shoutPlusCreator = signal<ShoutPlusCreator | null>(null);
  readonly shoutPlusLoading = signal(false);
  readonly joiningShoutPlus = signal(false);
  readonly formatShoutPlusStatusLabel = formatShoutPlusBusinessCreatorStatusLabel;
  private lastShoutPlusMembershipKey: string | null = null;
  private readonly shoutPlusBrandSearchTrigger = new Subject<string>();

  readonly profileHasChanges = computed(() => {
    const baseline = this.editBaseline();
    if (!baseline || !this.editMode()) {
      return false;
    }

    this.creatorProfileModel();
    this.creatorHandleEdits();

    return !savableSnapshotsEqual(this.extractSavableSnapshot(), baseline.savable);
  });

  readonly canSaveProfile = computed(() => this.profileHasChanges() && !this.savingState());
  readonly canResetProfile = computed(() => this.profileHasChanges() && !this.savingState());

  readonly emailHasChanges = computed(() => {
    const creator = this.creator();
    const brandUser = this.brandUser();
    if ((!creator && !brandUser) || !this.editMode()) {
      return false;
    }
    this.creatorProfileModel();
    const current = (this.creatorProfileModel().email ?? '').trim().toLowerCase();
    const baseline = (creator?.email ?? brandUser?.email ?? '').trim().toLowerCase();
    return current !== baseline && current.length > 0;
  });

  readonly canSaveEmail = computed(() => this.emailHasChanges() && !this.emailSavingState());
  readonly canSendPasswordReset = computed(
    () => this.emailJustUpdated() && !this.passwordResetSendingState() && !this.emailSavingState(),
  );

  readonly statusFlagsHasChanges = computed(() => {
    const creator = this.creator();
    const draft = this.statusFlagsDraft();
    if (!creator || !draft || !this.editMode()) {
      return false;
    }
    return (
      draft.isActive !== !!creator.isActive ||
      draft.mobileVerified !== !!creator.mobileVerified ||
      draft.isDeleted !== !!creator.isDeleted
    );
  });

  readonly canSaveStatusFlags = computed(
    () => this.statusFlagsHasChanges() && !this.statusFlagsSavingState(),
  );

  readonly showRerunModeration = computed(() => {
    const status = this.creator()?.moderationStaus;
    return (
      status !== CreatorModerationStatusEnum.readyToStart &&
      status !== CreatorModerationStatusEnum.rejected
    );
  });

  readonly canRerunModeration = computed(
    () =>
      !!this.creator() &&
      this.editMode() &&
      this.showRerunModeration() &&
      !this.moderationRerunningState(),
  );

  readonly moderationStatusLabel = computed(() =>
    this.getModerationStatusName(this.creator()?.moderationStaus),
  );

  readonly moderationStatusColorClass = computed(() =>
    this.getModerationStatusColorClass(this.creator()?.moderationStaus),
  );

  readonly moderationFailureReasons = computed(() => {
    const moderation = this.latestModeration();
    const status = this.creator()?.moderationStaus;
    if (!moderation || status === CreatorModerationStatusEnum.readyToStart) {
      return [];
    }
    return this.buildModerationFailureReasons(moderation);
  });

  constructor() {
    const focused = this.accountService.selectedBrandSignal();
    if (focused?.id) {
      this.selectedShoutPlusBrand.set({
        id: focused.id,
        companyName: focused.companyName?.trim() || focused.id,
        companyLogo: focused.companyLogo,
      });
    }

    effect(() => {
      const creatorId = this.creator()?.blazeFirebaseUid ?? this.userId() ?? null;
      const businessId = this.selectedBusinessId();

      const key = creatorId && businessId ? `${businessId}:${creatorId}` : null;
      if (key === this.lastShoutPlusMembershipKey) {
        return;
      }
      this.lastShoutPlusMembershipKey = key;
      void this.loadShoutPlusMembership(creatorId, businessId);
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.subscriptionList.add(
      this.shoutPlusBrandSearchTrigger
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => this.shoutPlusBrandSearchLoading.set(true)),
          switchMap((term) => this.searchShoutPlusBrands(term)),
        )
        .subscribe({
          next: (response) => {
            this.shoutPlusBrandResults.set(response.data ?? []);
            this.shoutPlusBrandSearchLoading.set(false);
            this.shoutPlusBrandPickerOpen.set(true);
          },
          error: () => {
            this.shoutPlusBrandResults.set([]);
            this.shoutPlusBrandSearchLoading.set(false);
          },
        }),
    );

    const fromInput = this.userId();
    if (fromInput) {
      this.fetchProfile(fromInput);
      return;
    }

    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => {
        const userId = params.get('userId');
        if (userId) {
          this.fetchProfile(userId);
        }
      }),
    );
  }

  backToCreatorsFilter(event: Event): void {
    event.preventDefault();

    if (this.creatorsFilterReturnUrl) {
      void this.router.navigateByUrl(this.creatorsFilterReturnUrl);
      return;
    }

    const id =
      this.creator()?.blazeFirebaseUid ??
      this.route.snapshot.paramMap.get('userId') ??
      undefined;
    void this.router.navigate(['/creators-v4-filter'], {
      queryParams: id ? { creatorId: id } : {},
    });
  }

  backToProfileReturn(event: Event): void {
    event.preventDefault();
    if (this.profileReturnUrl) {
      void this.router.navigateByUrl(this.profileReturnUrl);
      return;
    }
    void this.router.navigate(['/insights']);
  }

  toggleEditMode(): void {
    if (this.editMode()) {
      const creator = this.creator();
      const brandUser = this.brandUser();
      if (creator) {
        this.patchFormFromCreator(creator, this.deliveryAddress());
        this.initCreatorHandleEdits(creator);
      } else if (brandUser) {
        this.patchFormFromBrandUser(brandUser);
      }
      this.editBaseline.set(null);
      this.statusFlagsDraft.set(null);
      this.emailJustUpdated.set(false);
      this.editMode.set(false);
      return;
    }

    const creator = this.creator();
    const brandUser = this.brandUser();
    if (creator) {
      this.patchFormFromCreator(creator, this.deliveryAddress());
      this.initCreatorHandleEdits(creator);
      this.syncEditBaseline();
      this.syncStatusFlagsDraft(creator);
    } else if (brandUser) {
      this.patchFormFromBrandUser(brandUser);
    }
    this.emailJustUpdated.set(false);
    this.editMode.set(true);
  }

  saveProfile(): void {
    const creator = this.creator();
    if (!creator || !this.canSaveProfile()) {
      return;
    }

    const model = this.creatorProfileModel();
    const handles = this.creatorHandleEdits();
    this.savingState.set(true);

    this.subscriptionList.add(
      this.creatorService
        .updateCreator(creator.blazeFirebaseUid, {
          bio: model.bio,
          rank: model.rank,
          firstName: model.firstName,
          lastName: model.lastName,
          mobile: model.mobile,
          homeTownName: model.homeTownName,
          countryCode: model.countryCode,
          currencyCode: model.currencyCode,
          instaHandle: handles.instagram,
          tikTokHandle: handles.tiktok,
          youtubeHandle: handles.youtube,
          snapchatHandle: handles.snapchat,
        })
        .subscribe({
          next: (updated) => {
            const savedCreator = updated as CreatorResponseDto;
            this.creator.set(savedCreator);
            this.patchFormFromCreator(savedCreator, this.deliveryAddress());
            this.initCreatorHandleEdits(savedCreator);
            this.editBaseline.set(null);
            this.savingState.set(false);
            this.editMode.set(false);
            void this.toastNotificationService.alert({
              title: 'Profile updated',
              message: 'Creator profile changes were saved successfully.',
              type: 'success',
              icon: 'success',
            });
          },
          error: (err: { error?: { message?: string } }) => {
            this.savingState.set(false);
            void this.toastNotificationService.alert({
              title: 'Profile update failed',
              message: err?.error?.message ?? 'Unable to save profile changes. Please try again.',
              type: 'error',
              icon: 'error',
            });
          },
        }),
    );
  }

  saveEmail(): void {
    const creator = this.creator();
    const brandUser = this.brandUser();
    if ((!creator && !brandUser) || !this.canSaveEmail()) {
      return;
    }

    const email = (this.creatorProfileModel().email ?? '').trim().toLowerCase();
    this.emailSavingState.set(true);
    this.emailJustUpdated.set(false);

    if (creator) {
      this.subscriptionList.add(
        this.adminCreatorService.updateEmail(creator.blazeFirebaseUid, email).subscribe({
          next: (result) => {
            this.creator.set(result.creator);
            this.patchFormFromCreator(result.creator, this.deliveryAddress());
            this.emailSavingState.set(false);
            this.emailJustUpdated.set(true);
            void this.toastNotificationService.alert({
              title: 'Email updated',
              message: `Updated to ${result.firebaseEmail} in Firebase and the creator record.`,
              type: 'success',
              icon: 'success',
            });
          },
          error: (err: { error?: { message?: string } }) => {
            this.emailSavingState.set(false);
            void this.toastNotificationService.alert({
              title: 'Email update failed',
              message: err?.error?.message ?? 'Unable to update email. Please try again.',
              type: 'error',
              icon: 'error',
            });
          },
        }),
      );
      return;
    }

    const uid = brandUser?.firebaseUid;
    if (!uid) {
      this.emailSavingState.set(false);
      return;
    }

    this.subscriptionList.add(
      this.userService.updateEmailAsAdmin(uid, email).subscribe({
        next: (result) => {
          this.brandUser.set(result.user);
          this.patchFormFromBrandUser(result.user);
          this.emailSavingState.set(false);
          this.emailJustUpdated.set(true);
          void this.toastNotificationService.alert({
            title: 'Email updated',
            message: `Updated to ${result.firebaseEmail} in Firebase and the user record.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string } }) => {
          this.emailSavingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Email update failed',
            message: err?.error?.message ?? 'Unable to update email. Please try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  sendPasswordReset(): void {
    const creator = this.creator();
    const brandUser = this.brandUser();
    const uid = creator?.blazeFirebaseUid ?? brandUser?.firebaseUid;
    if (!uid || !this.canSendPasswordReset()) {
      return;
    }

    this.passwordResetSendingState.set(true);

    const request$ = creator
      ? this.adminCreatorService.sendPasswordReset(uid)
      : this.userService.sendPasswordResetAsAdmin(uid);

    this.subscriptionList.add(
      request$.subscribe({
        next: (result) => {
          this.passwordResetSendingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Password reset sent',
            message: `A password reset email was sent to ${result.email}.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string } }) => {
          this.passwordResetSendingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Password reset failed',
            message: err?.error?.message ?? 'Unable to send password reset email.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  toggleStatusFlag(flag: keyof StatusFlagsDraft): void {
    if (!this.editMode()) {
      return;
    }

    this.statusFlagsDraft.update((draft) => {
      if (!draft) {
        return draft;
      }
      return {
        ...draft,
        [flag]: !draft[flag],
      };
    });
  }

  saveStatusFlags(): void {
    const creator = this.creator();
    const draft = this.statusFlagsDraft();
    if (!creator || !draft || !this.canSaveStatusFlags()) {
      return;
    }

    this.statusFlagsSavingState.set(true);

    this.subscriptionList.add(
      this.creatorService
        .updateCreatorPartial(creator.blazeFirebaseUid, {
          isActive: draft.isActive,
          mobileVerified: draft.mobileVerified,
          isDeleted: draft.isDeleted,
        })
        .subscribe({
          next: (updated) => {
            const savedCreator = updated as CreatorResponseDto;
            this.creator.set(savedCreator);
            this.syncStatusFlagsDraft(savedCreator);
            this.statusFlagsSavingState.set(false);
            void this.toastNotificationService.alert({
              title: 'Status flags updated',
              message: 'Active, mobile verified, and deleted flags were saved.',
              type: 'success',
              icon: 'success',
            });
          },
          error: (err: { error?: { message?: string } }) => {
            this.statusFlagsSavingState.set(false);
            void this.toastNotificationService.alert({
              title: 'Status update failed',
              message: err?.error?.message ?? 'Unable to update status flags. Please try again.',
              type: 'error',
              icon: 'error',
            });
          },
        }),
    );
  }

  rerunModeration(): void {
    const creator = this.creator();
    if (!creator || !this.canRerunModeration()) {
      return;
    }

    this.moderationRerunningState.set(true);

    this.subscriptionList.add(
      this.creatorService.forceModeration(creator.blazeFirebaseUid).subscribe({
        next: (response) => {
          const result = response as ForceModerationResponse;
          const updatedCreator = result?.data?.[0];
          if (updatedCreator) {
            this.creator.set(updatedCreator);
            if (this.editMode()) {
              this.syncStatusFlagsDraft(updatedCreator);
            }
          }

          this.moderationRerunningState.set(false);
          this.fetchLatestModeration(creator.blazeFirebaseUid);

          const status = updatedCreator?.moderationStaus ?? creator.moderationStaus;
          const statusName = this.getModerationStatusName(status);
          const failed = status === CreatorModerationStatusEnum.rejected;

          void this.toastNotificationService.alert({
            title: failed ? 'Moderation failed' : 'Moderation re-run',
            message: failed
              ? `Creator moderation finished as ${statusName}. See reasons below.`
              : `Creator moderation status is now ${statusName}.`,
            type: failed ? 'error' : 'success',
            icon: failed ? 'error' : 'success',
          });
        },
        error: (err: { error?: { message?: string } }) => {
          this.moderationRerunningState.set(false);
          void this.toastNotificationService.alert({
            title: 'Moderation re-run failed',
            message: err?.error?.message ?? 'Unable to re-run moderation. Please try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  getModerationStatusName(status: CreatorModerationStatusEnum | null | undefined): string {
    switch (status) {
      case CreatorModerationStatusEnum.All:
        return 'All';
      case CreatorModerationStatusEnum.Unreviewed:
        return 'Unreviewed';
      case CreatorModerationStatusEnum.InReview:
        return 'In review';
      case CreatorModerationStatusEnum.NeedMoreInfo:
        return 'Need more info';
      case CreatorModerationStatusEnum.needHumanApproval:
        return 'Needs human approval';
      case CreatorModerationStatusEnum.readyToStart:
        return 'Ready to start';
      case CreatorModerationStatusEnum.rejected:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }

  getModerationStatusColorClass(status: CreatorModerationStatusEnum | null | undefined): string {
    switch (status) {
      case CreatorModerationStatusEnum.readyToStart:
        return 'text-green-600 dark:text-green-400';
      case CreatorModerationStatusEnum.rejected:
        return 'text-red-600 dark:text-red-400';
      case CreatorModerationStatusEnum.All:
      case CreatorModerationStatusEnum.Unreviewed:
      case CreatorModerationStatusEnum.InReview:
      case CreatorModerationStatusEnum.NeedMoreInfo:
      case CreatorModerationStatusEnum.needHumanApproval:
      default:
        return 'text-amber-600 dark:text-amber-400';
    }
  }

  booleanFlagColorClass(value: boolean | null | undefined): string {
    return value
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';
  }

  deletedFlagColorClass(value: boolean | null | undefined): string {
    return value
      ? 'text-red-600 dark:text-red-400'
      : 'text-green-600 dark:text-green-400';
  }

  statusSwitchTrackClass(
    value: boolean | null | undefined,
    variant: 'default' | 'danger' = 'default',
  ): string {
    const base =
      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-60';
    if (variant === 'danger') {
      return [base, value ? 'bg-red-600' : 'bg-zinc-300 dark:bg-zinc-600'].join(' ');
    }
    return [base, value ? 'bg-emerald-600' : 'bg-zinc-300 dark:bg-zinc-600'].join(' ');
  }

  statusSwitchThumbClass(value: boolean | null | undefined): string {
    return [
      'inline-block size-5 rounded-full bg-white shadow transition-transform',
      value ? 'translate-x-5' : 'translate-x-0.5',
    ].join(' ');
  }

  statusFlagIconClass(value: boolean | null | undefined): string {
    return ['mt-0.5 shrink-0', this.booleanFlagColorClass(value)].join(' ');
  }

  deletedFlagIconClass(value: boolean | null | undefined): string {
    return ['mt-0.5 shrink-0', this.deletedFlagColorClass(value)].join(' ');
  }

  moderationStatusIconClass(): string {
    return ['mt-0.5 shrink-0', this.moderationStatusColorClass()].join(' ');
  }

  booleanFlagLabel(value: boolean | null | undefined): string {
    return value ? 'True' : 'False';
  }

  resetProfileEdits(): void {
    if (!this.canResetProfile()) {
      return;
    }

    const creator = this.creator();
    if (!creator) {
      return;
    }

    this.patchFormFromCreator(creator, this.deliveryAddress());
    this.initCreatorHandleEdits(creator);
  }

  displayValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  }

  countryOptionFor(code: string | null | undefined): FlagSelectOption<CountryCodeOption> | null {
    const normalized = normalizeCountryCode(code);
    return this.countryOptions.find((option) => option.value === normalized) ?? null;
  }

  currencyOptionFor(code: string | null | undefined): FlagSelectOption<CurrencyCodeOption> | null {
    const normalized = normalizeCurrencyCode(code);
    return this.currencyOptions.find((option) => option.value === normalized) ?? null;
  }

  onCountrySelected(value: CountryCodeOption): void {
    this.creatorProfileModel.update((model) => ({
      ...model,
      countryCode: value,
    }));
  }

  onCurrencySelected(value: CurrencyCodeOption): void {
    this.creatorProfileModel.update((model) => ({
      ...model,
      currencyCode: value,
    }));
  }

  async onProfilePhotoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) {
      return;
    }

    const creator = this.creator();
    if (!creator || this.photoUploadingState()) {
      input.value = '';
      return;
    }

    this.photoUploadingState.set(true);

    try {
      const uploadedFile = await this.uploadService.uploadFile(
        files,
        S3BucketEnum.PROFILE_PHOTOS,
        ImageTypesEnum.GALLERY,
      );

      if (!uploadedFile?.fileKey) {
        void this.toastNotificationService.alert({
          title: 'Photo upload failed',
          message: 'Unable to upload the selected image. Please try again.',
          type: 'error',
          icon: 'error',
        });
        return;
      }

      const updated = (await firstValueFrom(
        this.creatorService.updateCreator(creator.blazeFirebaseUid, {
          photoURL: uploadedFile.fileKey,
        }),
      )) as CreatorResponseDto;

      this.creator.set(updated);
      void this.toastNotificationService.alert({
        title: 'Profile photo updated',
        message: 'The creator profile picture was saved successfully.',
        type: 'success',
        icon: 'success',
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'error' in err
          ? (err as { error?: { message?: string } }).error?.message
          : undefined;
      void this.toastNotificationService.alert({
        title: 'Profile photo update failed',
        message: message ?? 'Unable to save the profile picture. Please try again.',
        type: 'error',
        icon: 'error',
      });
    } finally {
      this.photoUploadingState.set(false);
      input.value = '';
    }
  }

  getSuperSocialAccount(platform: SocialPlatformKey): BriefTrackerSuperSocialAccount | null {
    if (platform === 'snapchat') {
      return null;
    }
    const profile = this.superSocialProfile();
    if (!profile?.found) {
      return null;
    }
    return profile.accounts.find((account) => account.platform === platform) ?? null;
  }

  getCreatorHandle(platform: SocialPlatformKey, creator = this.creator()): string {
    if (!creator) {
      return '';
    }
    switch (platform) {
      case 'instagram':
        return creator.instaHandle ?? '';
      case 'tiktok':
        return creator.tikTokHandle ?? '';
      case 'youtube':
        return creator.youtubeHandle ?? '';
      case 'snapchat':
        return creator.snapchatHandle ?? '';
      default:
        return '';
    }
  }

  getCreatorFollowers(platform: SocialPlatformKey, creator = this.creator()): number {
    if (!creator) {
      return 0;
    }
    switch (platform) {
      case 'instagram':
        return creator.instaFollowers ?? 0;
      case 'tiktok':
        return creator.tikTokFollowers ?? 0;
      case 'youtube':
        return creator.youtubeSubscribers ?? 0;
      case 'snapchat':
        return creator.snapchatFollowers ?? 0;
      default:
        return 0;
    }
  }

  getCreatorHandleEdit(platform: SocialPlatformKey): string {
    return this.creatorHandleEdits()[platform] ?? '';
  }

  onCreatorHandleEdit(platform: SocialPlatformKey, value: string): void {
    const normalized = normalizeSuperSocialHandleInput(value);
    this.creatorHandleEdits.update((state) => ({ ...state, [platform]: normalized }));

    const account = this.getSuperSocialAccount(platform);
    if (account) {
      const superSocialHandle = superSocialAccountHandleValue(account);
      const matches = superSocialHandlesMatch(normalized, superSocialHandle);
      this.syncFromSuperSocial.update((state) => ({ ...state, [platform]: matches && !!superSocialHandle }));
    }
  }

  isSyncFromSuperSocial(platform: SocialPlatformKey): boolean {
    return this.syncFromSuperSocial()[platform] === true;
  }

  onSyncFromSuperSocial(platform: SocialPlatformKey, checked: boolean): void {
    this.syncFromSuperSocial.update((state) => ({ ...state, [platform]: checked }));
    if (!checked) {
      return;
    }

    const account = this.getSuperSocialAccount(platform);
    if (!account) {
      return;
    }

    const handle = superSocialAccountHandleValue(account);
    this.creatorHandleEdits.update((state) => ({ ...state, [platform]: handle }));
  }

  getHandleMatchStatus(platform: SocialPlatformConfig): {
    tone: 'match' | 'mismatch' | 'no-super-social' | 'missing-creator' | 'missing-super-social' | 'snapchat';
    message: string;
  } {
    if (!platform.superSocialSupported) {
      return {
        tone: 'snapchat',
        message: 'Snapchat is stored on the creator record only — not managed by SuperSocial.',
      };
    }

    const account = this.getSuperSocialAccount(platform.key);
    const creatorHandle = normalizeSuperSocialHandleInput(this.getCreatorHandle(platform.key));
    const superSocialHandle = account ? normalizeSuperSocialHandleInput(superSocialAccountHandleValue(account)) : '';

    if (!account) {
      if (!creatorHandle) {
        return {
          tone: 'no-super-social',
          message: 'No SuperSocial account connected and no creator handle stored.',
        };
      }
      return {
        tone: 'no-super-social',
        message: `No SuperSocial ${platform.label} account found. Creator record has @${creatorHandle}.`,
      };
    }

    if (!creatorHandle && superSocialHandle) {
      return {
        tone: 'missing-creator',
        message: `Creator record is empty. SuperSocial has @${superSocialHandle}.`,
      };
    }

    if (creatorHandle && !superSocialHandle) {
      return {
        tone: 'missing-super-social',
        message: `SuperSocial handle is empty. Creator record has @${creatorHandle}.`,
      };
    }

    if (superSocialHandlesMatch(creatorHandle, superSocialHandle)) {
      return {
        tone: 'match',
        message: `Creator record matches SuperSocial (@${superSocialHandle}).`,
      };
    }

    return {
      tone: 'mismatch',
      message: `Mismatch — creator @${creatorHandle} vs SuperSocial @${superSocialHandle}.`,
    };
  }

  isSuperSocialRefreshLoading(accountId: string): boolean {
    return this.superSocialRefreshLoading()[accountId] === true;
  }

  isSuperSocialRefreshSent(accountId: string): boolean {
    return this.superSocialRefreshSent()[accountId] === true;
  }

  isRemoveAccountLoading(accountId: string): boolean {
    return this.removeAccountLoading()[accountId] === true;
  }

  async removeSuperSocialAccount(account: BriefTrackerSuperSocialAccount): Promise<void> {
    const externalId = this.creator()?.blazeFirebaseUid?.trim() ?? '';
    if (!externalId) {
      void this.toastNotificationService.alert({
        title: 'Missing creator',
        message: 'Creator Firebase UID is required to remove a SuperSocial account.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: `Remove ${account.platformLabel} account?`,
      message: `This permanently deletes the ${account.platformLabel} account, snapshots, and related SuperSocial data so a new account can be connected. This cannot be undone.`,
      confirmText: 'Remove account',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });
    if (!confirmed || this.isRemoveAccountLoading(account.id)) {
      return;
    }

    const request$ = this.buildRemoveAccountRequest(account, externalId);
    if (!request$) {
      void this.toastNotificationService.alert({
        title: 'Remove not supported',
        message: `No remove endpoint for ${account.platformLabel}.`,
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    this.removeAccountLoading.update((state) => ({ ...state, [account.id]: true }));

    this.subscriptionList.add(
      request$.subscribe({
        next: () => {
          this.removeAccountLoading.update((state) => {
            const next = { ...state };
            delete next[account.id];
            return next;
          });
          void this.toastNotificationService.alert({
            title: 'Account removed',
            message: `${account.platformLabel} account deleted. Reloading…`,
            type: 'success',
            icon: 'success',
          });
          this.fetchSuperSocialProfile(externalId);
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.removeAccountLoading.update((state) => {
            const next = { ...state };
            delete next[account.id];
            return next;
          });
          const nested = err?.error?.message;
          const message = Array.isArray(nested)
            ? nested.join(', ')
            : typeof nested === 'string' && nested.length
              ? nested
              : (err?.message ?? 'Request failed');
          void this.toastNotificationService.alert({
            title: `Remove ${account.platformLabel} failed`,
            message,
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  refreshSuperSocialAccount(account: BriefTrackerSuperSocialAccount): void {
    if (!supportsSuperSocialAccountRefresh(account.platform)) {
      return;
    }

    const request$ = this.buildSuperSocialRefreshRequest(account);
    if (!request$) {
      void this.toastNotificationService.alert({
        title: 'Refresh not supported',
        message: `No refresh orchestrator mapping for ${account.platform} source "${account.source ?? 'unknown'}".`,
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: true }));

    this.subscriptionList.add(
      request$.subscribe({
        next: () => {
          void this.toastNotificationService.alert({
            title: 'Refresh requested',
            message: `${account.platformLabel} refresh sent — waiting for SuperSocial to update…`,
            type: 'success',
            icon: 'success',
          });
          this.pollSuperSocialAccountUpdate(account);
        },
        error: () => {
          this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
          void this.toastNotificationService.alert({
            title: `Error refreshing ${account.platformLabel} account`,
            message: 'Something went wrong. If the problem persists, please contact support.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private pollSuperSocialAccountUpdate(account: BriefTrackerSuperSocialAccount): void {
    const creatorId = this.creator()?.blazeFirebaseUid?.trim() ?? '';
    if (!creatorId) {
      this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
      return;
    }

    const previousUpdatedIso = account.updatedDateIso;
    const accountId = account.id;
    const pollIntervalMs = 10000;
    const maxAttempts = 30;

    this.subscriptionList.add(
      timer(pollIntervalMs, pollIntervalMs)
        .pipe(
          take(maxAttempts),
          switchMap(() => this.trackerService.getSuperSocialProfile(creatorId)),
          map((profile) => ({
            profile,
            updated: superSocialAccountWasUpdated(profile, accountId, previousUpdatedIso),
          })),
          takeWhile((pollResult) => !pollResult.updated, true),
          last(),
        )
        .subscribe({
          next: ({ profile, updated }) => {
            this.superSocialRefreshLoading.update((state) => ({ ...state, [accountId]: false }));
            if (updated) {
              this.superSocialProfile.set(profile);
              void this.toastNotificationService.alert({
                title: 'Account refreshed',
                message: `${account.platformLabel} data updated in SuperSocial.`,
                type: 'success',
                icon: 'success',
              });
              return;
            }
            this.superSocialRefreshSent.update((state) => ({ ...state, [accountId]: true }));
            void this.toastNotificationService.alert({
              title: 'Refresh still in progress',
              message: `${account.platformLabel} orchestrator accepted the request but updatedDate has not changed yet. Reload the page in a few minutes.`,
              type: 'warning',
              icon: 'warning',
            });
          },
          error: () => {
            this.superSocialRefreshLoading.update((state) => ({ ...state, [accountId]: false }));
            this.superSocialRefreshSent.update((state) => ({ ...state, [accountId]: true }));
          },
        }),
    );
  }

  private buildSuperSocialRefreshRequest(
    account: BriefTrackerSuperSocialAccount,
  ): Observable<unknown> | null {
    const source = (account.source ?? '').toLowerCase();

    if (account.platform === 'instagram') {
      if (source === 'meta_api') {
        return this.superSocialService.refreshInstragramAuthentic(account.id);
      }
      if (source === 'apify' || !source) {
        return this.superSocialService.refreshInstragramApify(account.id);
      }
      return null;
    }

    if (account.platform === 'tiktok') {
      if (source === 'tiktok_api') {
        return this.superSocialService.refreshTiktokTikAuthentic(account.id);
      }
      if (source === 'tik_api' || source === 'apify' || !source) {
        return this.superSocialService.refreshTiktokTikApi(account.id);
      }
      return null;
    }

    if (account.platform === 'youtube') {
      return this.superSocialService.refreshYoutubeAuthentic(account.id);
    }

    return null;
  }

  private buildRemoveAccountRequest(
    account: BriefTrackerSuperSocialAccount,
    externalId: string,
  ): Observable<unknown> | null {
    const businessId = environment.superSocial.businessId;

    if (account.platform === 'tiktok') {
      return this.superSocialService.removeTiktokAccount({
        externalId,
        businessId,
        tiktokAccountId: account.id,
      });
    }

    if (account.platform === 'instagram') {
      return this.superSocialService.removeInstagramAccount({
        externalId,
        businessId,
        instagramAccountId: account.id,
      });
    }

    if (account.platform === 'youtube') {
      return this.superSocialService.removeYoutubeAccount({
        externalId,
        businessId,
        youtubeAccountId: account.id,
      });
    }

    return null;
  }

  private fetchProfile(userId: string): void {
    this.loadingState.set('loading');
    this.editMode.set(false);
    this.editBaseline.set(null);
    this.statusFlagsDraft.set(null);
    this.latestModeration.set(null);
    this.emailJustUpdated.set(false);
    this.creator.set(null);
    this.brandUser.set(null);
    this.deliveryAddress.set(null);
    this.superSocialProfile.set(null);
    this.superSocialRefreshLoading.set({});
    this.superSocialRefreshSent.set({});
    this.removeAccountLoading.set({});

    this.subscriptionList.add(
      forkJoin({
        creator: this.creatorService.getCreatorById(userId).pipe(
          map((creator) => creator ?? null),
          catchError(() => of(null)),
        ),
        user: this.userService.getUser(userId).pipe(
          map((user) => user ?? null),
          catchError(() => of(null)),
        ),
      }).subscribe({
        next: ({ creator, user }) => {
          if (creator) {
            this.creator.set(creator);
            this.patchFormFromCreator(creator, null);
            this.initCreatorHandleEdits(creator);
            this.loadingState.set('loaded');
            this.fetchDeliveryAddress(userId);
            this.fetchSuperSocialProfile(userId);
            this.fetchLatestModeration(userId);
            return;
          }

          if (user) {
            this.brandUser.set(user);
            this.patchFormFromBrandUser(user);
            this.loadingState.set('loaded');
            return;
          }

          this.loadingState.set('error');
        },
        error: () => {
          this.loadingState.set('error');
        },
      }),
    );
  }

  private patchFormFromBrandUser(user: UserResponseDto): void {
    this.creatorProfileModel.update((model) => ({
      ...model,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
    }));
  }

  private syncStatusFlagsDraft(creator: CreatorResponseDto): void {
    this.statusFlagsDraft.set({
      isActive: !!creator.isActive,
      mobileVerified: !!creator.mobileVerified,
      isDeleted: !!creator.isDeleted,
    });
  }

  private fetchLatestModeration(creatorId: string): void {
    this.moderationHistoryLoading.set(true);
    this.subscriptionList.add(
      this.moderateService.getModerateHistory(creatorId).subscribe({
        next: (history) => {
          this.latestModeration.set(history?.[0] ?? null);
          this.moderationHistoryLoading.set(false);
        },
        error: () => {
          this.latestModeration.set(null);
          this.moderationHistoryLoading.set(false);
        },
      }),
    );
  }

  private buildModerationFailureReasons(moderation: ModerationStatusDto): string[] {
    const reasons: string[] = [];
    const status = moderation.status;

    if (!moderation.profilePic) {
      reasons.push('Missing profile picture');
    }
    if (!moderation.firstName) {
      reasons.push('Missing first name');
    }
    if (!moderation.lastName) {
      reasons.push('Missing last name');
    }

    if (status === CreatorModerationStatusEnum.rejected) {
      return reasons.length > 0 ? reasons : ['Moderation rejected'];
    }

    if ((moderation.connectedAccounts ?? 0) <= 0) {
      reasons.push('No connected social accounts');
    } else if ((moderation.avgViews ?? 0) <= 1000 && (moderation.daysBetweenPosts ?? 100) >= 4) {
      reasons.push(`Average views too low (${moderation.avgViews ?? 0})`);
      reasons.push(`Posts too infrequent (${moderation.daysBetweenPosts ?? 0} days between posts)`);
    }

    return reasons;
  }

  private fetchSuperSocialProfile(creatorId: string): void {
    this.superSocialLoading.set(true);
    this.subscriptionList.add(
      this.trackerService.getSuperSocialProfile(creatorId).subscribe({
        next: (profile) => {
          this.superSocialProfile.set(profile);
          this.superSocialLoading.set(false);
        },
        error: () => {
          this.superSocialProfile.set(null);
          this.superSocialLoading.set(false);
        },
      }),
    );
  }

  private fetchDeliveryAddress(creatorId: string): void {
    this.subscriptionList.add(
      this.creatorService.getCreatorDefaultDeliveryAddress(creatorId).subscribe({
        next: (address) => {
          this.deliveryAddress.set(address);
          const creator = this.creator();
          if (creator) {
            this.patchFormFromCreator(creator, address);
          }
        },
        error: () => {
          this.deliveryAddress.set(null);
        },
      }),
    );
  }

  private initCreatorHandleEdits(creator: CreatorResponseDto): void {
    this.creatorHandleEdits.set({
      instagram: creator.instaHandle ?? '',
      tiktok: creator.tikTokHandle ?? '',
      youtube: creator.youtubeHandle ?? '',
      snapchat: creator.snapchatHandle ?? '',
    });
    this.syncFromSuperSocial.set({
      instagram: false,
      tiktok: false,
      youtube: false,
      snapchat: false,
    });
  }

  private patchFormFromCreator(
    creator: CreatorResponseDto,
    delivery: DeliveryAddressResponseDto | null,
  ): void {
    this.creatorProfileModel.set({
      bio: creator.bio ?? '',
      rank: creator.rank ?? 0,
      firstName: creator.firstName ?? '',
      lastName: creator.lastName ?? '',
      email: creator.email ?? '',
      mobile: creator.mobile ?? '',
      homeTownName: creator.homeTownName ?? '',
      countryCode: normalizeCountryCode(creator.countryCode),
      currencyCode: normalizeCurrencyCode(creator.currencyCode),
      deliveryFullName: delivery?.fullName ?? '',
      deliveryPostcode: delivery?.postcode ?? '',
      deliveryAddressLine1: delivery?.addressLine1 ?? '',
      deliveryAddressLine2: delivery?.addressLine2 ?? '',
      deliveryTown: delivery?.town ?? '',
      deliveryCountry: delivery?.country ?? '',
    });

    if (this.editMode()) {
      this.syncEditBaseline();
    }
  }

  private extractSavableSnapshot(): SavableProfileSnapshot {
    const model = this.creatorProfileModel();
    return {
      bio: model.bio,
      rank: model.rank,
      firstName: model.firstName,
      lastName: model.lastName,
      mobile: model.mobile,
      homeTownName: model.homeTownName,
      countryCode: model.countryCode,
      currencyCode: model.currencyCode,
      handles: normalizeSavableHandles(this.creatorHandleEdits()),
    };
  }

  private syncEditBaseline(): void {
    this.editBaseline.set({ savable: this.extractSavableSnapshot() });
  }

  onShoutPlusBrandSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.shoutPlusBrandSearch.set(value);
    this.shoutPlusBrandSearchTrigger.next(value.trim());
  }

  openShoutPlusBrandPicker(): void {
    this.shoutPlusBrandPickerOpen.set(true);
  }

  selectShoutPlusBrand(brand: BusinessAccount): void {
    this.selectedShoutPlusBrand.set({
      id: brand.id,
      companyName: brand.companyName?.trim() || brand.id,
      companyLogo: brand.companyLogo,
    });
    this.shoutPlusBrandSearch.set('');
    this.shoutPlusBrandResults.set([]);
    this.shoutPlusBrandPickerOpen.set(false);
  }

  clearShoutPlusBrand(): void {
    this.selectedShoutPlusBrand.set(null);
    this.shoutPlusCreator.set(null);
    this.lastShoutPlusMembershipKey = null;
    this.shoutPlusBrandSearch.set('');
    this.shoutPlusBrandResults.set([]);
    this.shoutPlusBrandPickerOpen.set(false);
  }

  private searchShoutPlusBrands(term: string): Observable<BrandResponseDto> {
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      this.shoutPlusBrandSearchLoading.set(false);
      this.shoutPlusBrandResults.set([]);
      return of({ count: 0, data: [] });
    }

    if (BUSINESS_ACCOUNT_UUID_RE.test(trimmed)) {
      return this.businessAccountService.getBusinessAccountById(trimmed).pipe(
        map((business) => ({ count: 1, data: [business] })),
        catchError(() => of({ count: 0, data: [] })),
      );
    }

    return this.businessAccountService
      .getInsightsBrands({
        take: 20,
        skip: 0,
        searchTerm: trimmed,
        sortField: 'companyName',
        sortOrder: 'ASC',
      })
      .pipe(catchError(() => of({ count: 0, data: [] })));
  }

  private async loadShoutPlusMembership(
    creatorId: string | null,
    businessId: string | null,
  ): Promise<void> {
    if (!creatorId || !businessId) {
      this.shoutPlusCreator.set(null);
      this.shoutPlusLoading.set(false);
      return;
    }

    this.shoutPlusLoading.set(true);

    try {
      const response = (await firstValueFrom(
        this.businessCreatorsService.filterBusinessCreators({
          creator_id: creatorId,
          business_account_id: businessId,
          take: 1,
        }),
      )) as { data?: ShoutPlusCreator[] } | null;

      const row = response?.data?.[0] ?? null;
      if (row) {
        const pill = shoutPlusBusinessCreatorStatusPillClass(row.shout_plus_creator_status);
        if (pill) {
          row.status_pill_class = pill;
        }
      }
      this.shoutPlusCreator.set(row);
    } catch (err) {
      console.warn('Fetching SHOUT+ business creator failed: ', err);
      this.shoutPlusCreator.set(null);
    } finally {
      this.shoutPlusLoading.set(false);
    }
  }

  async joinShoutPlus(): Promise<void> {
    const creatorId = this.creator()?.blazeFirebaseUid ?? this.userId();
    const businessId = this.selectedBusinessId();

    if (!creatorId || !businessId || this.joiningShoutPlus()) {
      return;
    }

    this.joiningShoutPlus.set(true);

    try {
      const settings = await firstValueFrom(
        this.invitesService.getAutoApproveSettingsForBusiness(businessId),
      );
      if (settings) {
        await this.emulateBusinessLink(businessId, creatorId);
      }
    } catch (error) {
      console.error('Failed to fetch auto-approve settings', error);
      if (error instanceof HttpErrorResponse && error.status === 404) {
        await this.toastNotificationService.alert({
          title: 'Auto-approve required',
          message: 'Go to the SHOUT+ dashboard first to set initial auto-approve settings.',
          type: 'warning',
          icon: 'warning',
        });
      } else {
        await this.toastNotificationService.alert({
          title: 'Join failed',
          message: 'Could not join this creator to SHOUT+. Please try again.',
          type: 'error',
          icon: 'error',
        });
      }
      this.joiningShoutPlus.set(false);
    }
  }

  private async emulateBusinessLink(businessAccountId: string, creatorId: string): Promise<void> {
    const payload: ShoutPlusCreator = {
      business_account_id: businessAccountId,
      shout_plus_creator_status: ShoutPlusBusinessCreatorStatusEnum.AWAITING_REVIEW,
      creator_id: creatorId,
      created_date: new Date(),
      updated_date: new Date(),
      createdDate: new Date(),
      isDeleted: false,
    };

    try {
      const response = (await firstValueFrom(
        this.businessCreatorsService.linkCreatorWithBusiness(payload),
      )) as ShoutPlusCreator;

      if (response) {
        const pill = shoutPlusBusinessCreatorStatusPillClass(response.shout_plus_creator_status);
        if (pill) {
          response.status_pill_class = pill;
        }
        this.shoutPlusCreator.set(response);
        this.lastShoutPlusMembershipKey = `${businessAccountId}:${creatorId}`;
      }

      await this.toastNotificationService.alert({
        title: 'SHOUT+',
        message: 'Business creator successfully added!',
        type: 'success',
        icon: 'success',
      });
    } catch (err) {
      console.error('Failed to create association: ', err);
      await this.toastNotificationService.alert({
        title: 'Join failed',
        message: 'Creation of business link failed!',
        type: 'error',
        icon: 'error',
      });
    } finally {
      this.joiningShoutPlus.set(false);
    }
  }

  async copyShoutPlusLinkId(linkId: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(linkId);
      await this.toastNotificationService.alert({
        title: 'Copied',
        message: 'SHOUT+ Link ID copied to clipboard!',
        type: 'success',
        icon: 'success',
      });
    } catch {
      await this.toastNotificationService.alert({
        title: 'Copy failed',
        message: 'Could not copy Link ID to clipboard.',
        type: 'error',
        icon: 'error',
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
